/**
 * Validation Helper Utilities
 * 
 * Provides consistent validation handling and error messaging across all forms
 */

import toast from 'react-hot-toast';

/**
 * Format validation errors for display
 * @param {Array} errors - Array of validation errors from Yup
 * @returns {Object} - Formatted errors object
 */
export const formatValidationErrors = (errors) => {
  const formattedErrors = {};
  
  if (errors && errors.inner) {
    errors.inner.forEach(error => {
      formattedErrors[error.path] = error.message;
    });
  }
  
  return formattedErrors;
};

/**
 * Display validation errors as toast notifications
 * @param {Array} errors - Array of validation errors from Yup
 * @param {string} title - Optional title for the error
 */
export const showValidationErrors = (errors, title = 'Validation Error') => {
  if (errors && errors.inner && errors.inner.length > 0) {
    // Show first error as toast
    toast.error(errors.inner[0].message);
    
    // If multiple errors, show count
    if (errors.inner.length > 1) {
      setTimeout(() => {
        toast.error(`${errors.inner.length - 1} more validation errors need attention`);
      }, 1000);
    }
  } else if (errors && errors.message) {
    toast.error(errors.message);
  } else {
    toast.error('Please check all required fields');
  }
};

/**
 * Validate form data with schema and show errors
 * @param {Object} schema - Yup validation schema
 * @param {Object} data - Form data to validate
 * @returns {boolean} - True if valid, false if invalid
 */
export const validateFormData = async (schema, data) => {
  try {
    await schema.validate(data, { abortEarly: false });
    return true;
  } catch (validationError) {
    showValidationErrors(validationError);
    return false;
  }
};

/**
 * Get field-specific error message
 * @param {Object} errors - Formik errors object
 * @param {Object} touched - Formik touched object
 * @param {string} fieldName - Name of the field
 * @returns {string|null} - Error message or null
 */
export const getFieldError = (errors, touched, fieldName) => {
  return touched[fieldName] && errors[fieldName] ? errors[fieldName] : null;
};

/**
 * Check if field has error
 * @param {Object} errors - Formik errors object
 * @param {Object} touched - Formik touched object
 * @param {string} fieldName - Name of the field
 * @returns {boolean} - True if field has error
 */
export const hasFieldError = (errors, touched, fieldName) => {
  return !!(touched[fieldName] && errors[fieldName]);
};

/**
 * Get success message for different operations
 * @param {string} operation - Type of operation (create, update, delete, etc.)
 * @param {string} entity - Entity name (patient, user, etc.)
 * @returns {string} - Success message
 */
export const getSuccessMessage = (operation, entity = 'record') => {
  const messages = {
    create: `${entity.charAt(0).toUpperCase() + entity.slice(1)} created successfully!`,
    update: `${entity.charAt(0).toUpperCase() + entity.slice(1)} updated successfully!`,
    delete: `${entity.charAt(0).toUpperCase() + entity.slice(1)} deleted successfully!`,
    complete: `${entity.charAt(0).toUpperCase() + entity.slice(1)} registration completed successfully!`,
    save: `${entity.charAt(0).toUpperCase() + entity.slice(1)} saved successfully!`
  };
  
  return messages[operation] || `Operation completed successfully!`;
};

/**
 * Get error message for different operations
 * @param {string} operation - Type of operation (create, update, delete, etc.)
 * @param {string} entity - Entity name (patient, user, etc.)
 * @returns {string} - Error message
 */
export const getErrorMessage = (operation, entity = 'record') => {
  const messages = {
    create: `Failed to create ${entity}`,
    update: `Failed to update ${entity}`,
    delete: `Failed to delete ${entity}`,
    complete: `Failed to complete ${entity} registration`,
    save: `Failed to save ${entity}`,
    load: `Failed to load ${entity}`,
    fetch: `Failed to fetch ${entity} data`
  };
  
  return messages[operation] || `Operation failed`;
};

/**
 * Show success toast with consistent styling
 * @param {string} message - Success message
 * @param {Object} options - Toast options
 */
export const showSuccess = (message, options = {}) => {
  toast.success(message, {
    duration: 3000,
    position: 'top-right',
    ...options
  });
};

/**
 * Show error toast with consistent styling
 * @param {string} message - Error message
 * @param {Object} options - Toast options
 */
export const showError = (message, options = {}) => {
  toast.error(message, {
    duration: 4000,
    position: 'top-right',
    ...options
  });
};

/**
 * Show loading toast
 * @param {string} message - Loading message
 * @returns {string} - Toast ID for dismissal
 */
export const showLoading = (message = 'Processing...') => {
  return toast.loading(message, {
    position: 'top-right'
  });
};

/**
 * Dismiss loading toast
 * @param {string} toastId - Toast ID to dismiss
 */
export const dismissLoading = (toastId) => {
  if (toastId) {
    toast.dismiss(toastId);
  }
};

/**
 * Common phone number formatting
 * @param {string} phoneNumber - Raw phone number
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters except +
  let cleaned = phoneNumber.replace(/[^\d+]/g, '');
  
  // Ensure it starts with + if it's an international number
  if (cleaned.length > 10 && !cleaned.startsWith('+')) {
    cleaned = '+' + cleaned;
  }
  
  return cleaned;
};

/**
 * Validate Emirates ID format
 * @param {string} emiratesId - Emirates ID to validate
 * @returns {boolean} - True if valid format
 */
export const validateEmiratesId = (emiratesId) => {
  if (!emiratesId) return true; // Optional field
  
  const emiratesIdRegex = /^784\d{12}$/;
  return emiratesIdRegex.test(emiratesId);
};

/**
 * Validate international phone number
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} - True if valid format
 */
export const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return true; // Optional field
  
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;
  return phoneRegex.test(phoneNumber);
};

export default {
  formatValidationErrors,
  showValidationErrors,
  validateFormData,
  getFieldError,
  hasFieldError,
  getSuccessMessage,
  getErrorMessage,
  showSuccess,
  showError,
  showLoading,
  dismissLoading,
  formatPhoneNumber,
  validateEmiratesId,
  validatePhoneNumber
};